import React, { type MouseEventHandler, type PropsWithChildren, type Ref, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Loading } from '../loading';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'accent'
    | 'outline'
    | 'ghost-accent'
    | 'ghost-gray'
    | 'destructive'
    | 'orange'
    | 'gradient-premium';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLDivElement>;
  isDiv?: boolean;

  'data-testid'?: string;
}

const defaultClasses = [
  'flex',
  'justify-center',
  'items-center',
  'cursor-pointer',
  'rounded-md',
  'transition',
  'ease-in-out',
  'duration-200',
  'hover:outline-none',
  'h-fit',
  'box-border',
  'gap-2',
];

const variantClassNames = {
  accent:
    'shadow-1 bg-branding-accent-600 text-white hover:bg-branding-accent-700 focus:bg-branding-accent-600 focus:outline-none focus:ring-4 focus:ring-branding-accent-50 disabled:bg-branding-accent-200',
  outline:
    'shadow-1 border-branding-gray text-black border bg-white hover:bg-branding-gray-50  focus:outline-none focus:ring-4 focus:ring-branding-accent-50',
  'ghost-accent':
    'bg-transparent text-branding-accent-700 hover:bg-branding-accent-50 focus:outline-none',
  'ghost-gray': 'bg-transparent text-black hover:bg-branding-gray-100 focus:outline-none',
  destructive:
    'shadow-1 bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-4 focus:ring-red-50',
  orange:
    'shadow-1 bg-branding-accent-orange text-white hover:bg-orange-500 focus:outline-none focus:ring-4 focus:ring-orange-100',
  'gradient-premium':
    'shadow-1 bg-gradient-to-r from-branding-accent-600 to-purple-700 text-white focus:outline-none focus:ring-4 focus:ring-branding-accent-50 hover:from-branding-accent-700 hover:to-purple-900',
};

const loadingClassNames = {
  accent: 'border-white border-t-transparent',
  outline: 'border-branding-black border-t-transparent',
  'ghost-accent': 'border-branding-accent-700 border-t-transparent',
  'ghost-gray': 'border-branding-gray border-t-transparent',
  destructive: 'border-white border-t-transparent',
  orange: 'border-white border-t-transparent',
  'gradient-premium': 'border-white border-t-transparent',
};

const sizes = {
  xs: 'px-2.5 py-1.5 font-body-3-m',
  sm: 'px-2.5 py-1.5 font-body-2-sb min-h-8 leading-5',
  md: 'px-3.5 py-2 font-body-2-sb min-h-9 leading-5',
  lg: 'px-4 py-2.5 font-body-2-sb min-h-10 leading-5',
  xl: 'px-4.5 py-2.5 font-body-1-sb min-h-11 leading-6',
};

function getClasses(props: {
  className?: ButtonProps['className'];
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  disabled?: ButtonProps['disabled'];
  loading?: ButtonProps['loading'];
}) {
  const classNamesBySize = sizes[props.size ?? 'lg'];
  const classNamesByDisabled = props.disabled ? 'pointer-events-none opacity-50' : '';
  const classNamesByLoading = props.loading ? 'pointer-events-none' : '';
  const classNamesByVariant = variantClassNames[props.variant ?? 'accent'];

  return twMerge(
    ...defaultClasses,
    classNamesByVariant,
    classNamesBySize,
    classNamesByDisabled,
    classNamesByLoading,
    props.className ?? ''
  );
}

function ButtonContent(props: PropsWithChildren<ButtonProps>) {
  return (
    <>
      {props.loading ? (
        <div className="relative flex w-full items-center justify-center">
          {/* Invisible children to maintain width */}
          <div className="opacity-0">{props.children}</div>

          <div className="absolute inset-0 flex items-center justify-center">
            <Loading size="sm" className={loadingClassNames[props.variant ?? 'accent']} />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {props.leftIcon != null ? props.leftIcon : null}
          {props.children}
          {props.rightIcon != null ? props.rightIcon : null}
        </div>
      )}
    </>
  );
}

export const Button = forwardRef(function Button(
  props: PropsWithChildren<ButtonProps>,
  ref: Ref<HTMLDivElement | HTMLButtonElement>
) {
  const className = getClasses(props);

  if (props.isDiv) {
    return (
      <div className={className} onClick={props.onClick} ref={ref as Ref<HTMLDivElement>}>
        <ButtonContent {...props} />
      </div>
    );
  }

  return (
    <button
      id={props.id != null ? props.id : undefined}
      className={className}
      onClick={(evt) => {
        if (props.disabled || props.loading) {
          return;
        }

        if (props.onClick != null) {
          props.onClick(evt);
        }
      }}
      disabled={props.disabled ? true : undefined}
      type={props.type ?? 'button'}
      tabIndex={-1}
      ref={ref as Ref<HTMLButtonElement>}
      data-testid={props['data-testid']}
    >
      <ButtonContent {...props} />
    </button>
  );
});

export function ButtonLink(props: ButtonProps) {
  return (
    <button
      id={props.id != null ? props.id : undefined}
      className={twMerge(
        defaultClasses,
        'font-body-2-r text-branding-accent underline hover:text-branding-accent-800 focus:text-branding-accent active:text-branding-accent-800 disabled:cursor-default disabled:text-branding-gray-200'
      )}
      onClick={(evt) => {
        if (props.disabled || props.loading) {
          return;
        }

        if (props.onClick != null) {
          props.onClick(evt);
        }
      }}
      disabled={props.disabled ? true : undefined}
      type={props.type ?? 'button'}
      tabIndex={-1}
      data-testid={props['data-testid']}
    >
      {props.children}
    </button>
  );
}
